// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { forwardRef } from 'react';
import { StyleProp, StyleSheet, TextInput, TextInputProps, TextStyle } from 'react-native';

import appColors from '@/theme/colors';

type GoffyInputProps = Omit<TextInputProps, 'style' | 'placeholderTextColor'> & {
  style?: StyleProp<TextStyle>;
  /** Override only when a field needs an unusual placeholder tone; the default is readable everywhere. */
  placeholderTextColor?: string;
  /** `compact` for fields sitting inline in a dense row, where the default padding grows the row. */
  size?: 'default' | 'compact';
};

/**
 * The app's text field.
 *
 * Exists because hand-rolled inputs kept coming out invisible: a tonal border over a card of nearly
 * the same colour has no visible edge, so the field reads as body text. Screens were writing
 * `borderColor: appColors.border.default` — a dark green at 15% opacity — over a background matching
 * the panel behind it, which composites to a difference of about two of 255 per channel.
 *
 * So the border is the accent colour, which stays legible on any surface, and the fill is
 * `surface.input`, which differs from the `#07200D` cards these sit on. Belt and braces: either
 * alone would be enough on most screens, but the failures happened exactly where one was relied on.
 *
 * `placeholderTextColor` lives here too — passing the same faint border token as a placeholder colour
 * is what made one profile field's placeholder unreadable.
 *
 * Deliberately imports no router: `GoffyButton` calls `useGlobalSearchParams`, which is why every
 * test rendering a button must mock `expo-router`. A text field has no reason to tax form tests
 * with that.
 */
/**
 * Forwards its ref: a field that opens a modal has to be able to `blur()` itself when the modal
 * hands focus back, so the caller needs the underlying TextInput.
 */
const GoffyInput = forwardRef<TextInput, GoffyInputProps>(function GoffyInput(
  { style, placeholderTextColor = appColors.text.muted, size = 'default', ...rest },
  ref
) {
  return (
    <TextInput
      {...rest}
      ref={ref}
      placeholderTextColor={placeholderTextColor}
      style={[styles.input, size === 'compact' ? styles.compact : styles.default, style]}
    />
  );
});

export default GoffyInput;

export { GoffyInput };

const styles = StyleSheet.create({
  input: {
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.surface.input,
    borderRadius: 12,
    color: appColors.text.primary,
    fontSize: 14,
  },
  default: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  compact: {
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
});
